import * as React from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Button, makeStyles, Popover, PopoverSurface, PopoverTrigger } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { useFormikContext } from 'formik'
import useAxios from '../../../../hooks/useAxios'
import { SortDirection } from '../../../../config/settingConfig'
import { AxiosResponse } from 'axios'
import BaseOverview, {
    BaseOverviewCount,
    BaseOverviewLineItemProps,
    BaseOverviewProps,
    useOverviewStyles,
} from '../../../../pages/BaseOverview/BaseOverview'
import { BaseOverviewFilterType, generateFromDate } from '../../../../pages/BaseOverview/BaseOverviewFilter'
import { BaseOverviewSortingType } from '../../../../pages/BaseOverview/BaseSortMenu'
import { Message } from '@microsoft/microsoft-graph-types'
import { EntityDto } from '../../../../config/appConfig'
import { FileEmailListItem } from './FileEmailListItem'
import { ViewFileProps } from '../ViewFile'
import { Destination, IGraphArrayPayload } from '../../../../context/AxiosContext'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../../../../helpers/referenceNumberHelper'
import BaseHeaderItem from '../../../../pages/BaseOverview/BaseHeaderItem'
import useUser from '../../../../hooks/useUser'
import HammockDialog from '../../../components/Dialog/HammockDialog'
import AuthorizationContext from '../../../../context/AuthorizationContext'
import PreviewMessageDialogBody, { PreviewMessageDialogBodyProps } from '../../../components/Dialog/Message/PreviewMessageDialogBody'
import FileEmailHeaderActionsButtons, { EmailHeaderActionsOverviewProps } from './FileEmailHeaderActionsButtons'
import FileEmailHeaderFilterTags, { EmailHeaderFilterTagsOverviewProps } from './FileEmailHeaderFilterTags'
import { bundleIcon, HatGraduationFilled, HatGraduationRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import DateRange from '../../../../components/DatePicker/DateRange'

const SearchHintIcon = bundleIcon(HatGraduationFilled, HatGraduationRegular)

export const useItemStyles = makeStyles({
    listEmailItem: {
        display: 'grid',
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailItemSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailItemSmall: {
        gridTemplateColumns: 'var(--baseOverviewAvatarWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailItemSmallSelectable: {
        gridTemplateColumns: 'var(--baseOverviewCheckboxWidth) var(--baseOverviewAvatarWidth) auto var(--baseOverviewDateWidth)',
    },
    listEmailDetails: {
        display: 'grid',
        gridTemplateColumns: '1fr`',
        gridTemplateRows: '1fr 1fr 1fr`',
    },
})

export type ExtendedEmailOverviewDto = Omit<Message, 'id'> &
    EntityDto & {
        inbound?: boolean
        url?: string //Custom URL with which the item can be downloaded.
    }

export type EmailOverviewProps = BaseOverviewProps<
    ExtendedEmailOverviewDto,
    EmailFilterType,
    EmailSortingType,
    BaseOverviewCount,
    EmailHeaderActionsOverviewProps,
    EmailHeaderFilterTagsOverviewProps
> &
    ViewFileProps

export type EmailLineOverviewProps = EmailOverviewProps &
    BaseOverviewLineItemProps<ExtendedEmailOverviewDto, EmailFilterType, EmailSortingType> & {
        onPreviewClick: (message: ExtendedEmailOverviewDto) => void
    }

export type EmailFilterType = BaseOverviewFilterType & {
    from?: string
    to?: string
}

export type EmailSorting = {
    sentDateTime: string
}

export type EmailSortingType = BaseOverviewSortingType<EmailSorting>

function EmailFilter() {
    const { values, setFieldValue } = useFormikContext<EmailFilterType>()
    const { viewport, isViewport } = useViewport()

    const orientation = useMemo(() => (isViewport('<', Viewport.Large) ? 'vertical' : 'horizontal'), [viewport])

    const updateFieldValue = (field: string, value: any, shouldValidate?: boolean) => {
        setFieldValue('tag', undefined, false).then(() => setFieldValue(field, value, shouldValidate))
    }

    return (
        <>
            <DateRange
                orientation={orientation}
                startValue={values.from}
                endValue={values.to}
                changeStartDate={date => updateFieldValue('from', date)}
                changeEndDate={date => updateFieldValue('to', date)}
                showLabel
            />
        </>
    )
}

function FilterInputHint() {
    const { t: translate } = useTranslation()

    const [open, setOpen] = useState<boolean>(false)

    return (
        <Popover open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <PopoverTrigger disableButtonEnhancement>
                <Button size={'small'} appearance={'transparent'} icon={<SearchHintIcon />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
                <h3>{translate('file.pivot.email.search.hint.title')}</h3>
                <br />
                <div dangerouslySetInnerHTML={{ __html: translate('file.pivot.email.search.hint.body') }}></div>
            </PopoverSurface>
        </Popover>
    )
}

const headerSortingItems: EmailSortingType[] = [
    { property: ['sentDateTime'], direction: SortDirection.Ascending, descriptionKey: 'sorting.dateasc' },
    { property: ['sentDateTime'], direction: SortDirection.Descending, default: true, descriptionKey: 'sorting.datedesc' },
]

export default function FileEmailOverview(props: Partial<EmailOverviewProps> & ViewFileProps) {
    const axios = useAxios()
    const { host: currentHost } = useContext(AuthorizationContext)
    const itemStyles = useItemStyles()
    const overviewStyles = useOverviewStyles()
    const { currentMailbox } = useUser()

    const [emailReplicationFolderId, setEmailReplicationFolderId] = useState<string>()
    const [emailUserFolderId, setEmailUserFolderId] = useState<string>()

    const [selectedItems, setSelectedItems] = useState<ExtendedEmailOverviewDto[]>([])
    const [openPreviewMessage, setOpenPreviewMessage] = useState<ExtendedEmailOverviewDto>()

    useEffect(() => {
        axios
            .get('/email/replication/parent-folder/id', { params: { references: [props.file.reference] } })
            .then((response: AxiosResponse<{ [key: string]: string }>) => setEmailReplicationFolderId(response.data[props.file.reference] || 'inbox'))
    }, [props.file])

    useEffect(() => {
        axios
            .get(`/email/${currentMailbox}/folder/${props.file.reference}`, {
                baseURL: Destination.Hammock,
                headers: { SuppressErrors: 'true' },
            })
            .then((response: AxiosResponse<string>) => setEmailUserFolderId(response.data))
    }, [props.file])

    const defaultFilterValues = useMemo<EmailFilterType>(
        () => ({
            from: '',
            to: '',
            query: '',
            ...props.fixedFilterValues,
            ...props.defaultFilterValues,
        }),
        [props.fixedFilterValues, props.defaultFilterValues],
    )

    const countItems = useCallback(
        (column?: number, filterValues?: EmailFilterType): Promise<BaseOverviewCount> => {
            if (!emailReplicationFolderId) {
                setSelectedItems([])
                return Promise.resolve({ total: 0 })
            }
            if (filterValues.query) {
                setSelectedItems([])
                return Promise.resolve({ total: 20 })
            }

            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select: 'id',
                        $filter: [
                            `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                            filterValues.from && `sentDateTime ge ${filterValues.from}`,
                            filterValues.to && `sentDateTime le ${filterValues.to}`,
                        ]
                            .filter(Boolean)
                            .join(' and '),
                        $count: true,
                    },
                })
                .then(
                    (response: AxiosResponse<IGraphArrayPayload<Message>>): BaseOverviewCount => ({
                        total: response.data['@odata.count'],
                    }),
                )
        },
        [props.file, emailReplicationFolderId],
    )

    const loadItems = useCallback(
        (
            column?: number,
            filterValues?: EmailFilterType,
            sortingValues?: EmailSortingType,
            start?: number,
            size?: number,
        ): Promise<ExtendedEmailOverviewDto[]> => {
            return axios
                .get(`/mailFolders('${emailReplicationFolderId}')/messages`, {
                    baseURL: Destination.ReplicationEmail,
                    headers: { SuppressErrors: 'true', 'If-None-Match': null, Prefer: 'IdType="ImmutableId"' },
                    params: {
                        $select:
                            'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                        $filter: filterValues.query
                            ? undefined
                            : [
                                  `singleValueExtendedProperties/Any(ep: ep/id eq '${FILE_REFERENCE_NUMBER_PROP}' and contains(ep/value, '[${props.file.reference}]'))`,
                                  filterValues.from && `sentDateTime ge ${filterValues.from}`,
                                  filterValues.to && `sentDateTime le ${filterValues.to}`,
                              ]
                                  .filter(Boolean)
                                  .join(' and '),
                        $expand: filterValues.query ? undefined : `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
                        $top: `${size}`,
                        $skip: filterValues.query ? undefined : `${start}`,
                        $orderby: filterValues.query ? undefined : `${sortingValues.property[0]} ${sortingValues.direction.toLowerCase()}`,
                        $search: filterValues.query ? `"${filterValues.query} ${props.file.reference}"` : undefined,
                    },
                })
                .then((response: AxiosResponse<IGraphArrayPayload<Message>>): ExtendedEmailOverviewDto[] =>
                    (response.data.value || []).map((item: Message) => ({
                        id: item.id,
                        ...item,
                        references: extractReferenceNumbersFromMessage(item),
                        inbound:
                            item.sender?.emailAddress.address
                                .substring(item.sender?.emailAddress.address.indexOf('@'))
                                .localeCompare(currentMailbox?.substring(currentMailbox?.indexOf('@'))) !== 0,
                        url: `${process.env.INBOXER_HOST}/api/download-message?hostKey=${currentHost.RowKey}&email=${encodeURIComponent(currentMailbox)}&messageId=${item.id}`,
                    })),
                )
        },
        [props.file, emailReplicationFolderId],
    )

    return (
        <>
            <HammockDialog<PreviewMessageDialogBodyProps>
                open={openPreviewMessage !== undefined}
                setOpen={(open: boolean) => setOpenPreviewMessage(open ? openPreviewMessage : undefined)}
                dialogBody={PreviewMessageDialogBody}
                dialogBodyProps={() => ({
                    message: openPreviewMessage,
                    destination: Destination.ReplicationEmail,
                    userEmail: currentHost.ReplicationMail,
                })}
            />

            <BaseOverview<
                ExtendedEmailOverviewDto,
                EmailFilterType,
                EmailSortingType,
                BaseOverviewCount,
                EmailHeaderActionsOverviewProps,
                EmailHeaderFilterTagsOverviewProps
            >
                defaultFilterValues={defaultFilterValues}
                countItems={countItems}
                loadItems={loadItems}
                listItemHeaderContent={BaseHeaderItem}
                listItemHeaderClassnames={[overviewStyles.listBaseHeaderItem]}
                listItemContent={(props: EmailLineOverviewProps) => (
                    <FileEmailListItem {...props} onPreviewClick={item => setOpenPreviewMessage(item)} />
                )}
                listItemClassnames={props => [
                    props.onItemSelected ? itemStyles.listEmailItemSelectable : itemStyles.listEmailItem,
                    props.viewportNarrow && (props.onItemSelected ? itemStyles.listEmailItemSmallSelectable : itemStyles.listEmailItemSmall),
                ]}
                listColumnFullWidth
                headerActionsButtons={FileEmailHeaderActionsButtons}
                headerActionsButtonsProps={{
                    emailUserFolderId: emailUserFolderId,
                    setEmailUserFolderId: setEmailUserFolderId,
                    reload: () => {
                        setEmailReplicationFolderId(prev => `${prev}`)
                        setSelectedItems([])
                    },
                }}
                headerDefaultFilterTags={FileEmailHeaderFilterTags}
                headerDefaultFilterTagsProps={{
                    selectedItems: selectedItems,
                    reload: () => {
                        setEmailReplicationFolderId(prev => `${prev}`)
                        setSelectedItems([])
                    },
                }}
                headerSortingItems={headerSortingItems}
                filterContent={EmailFilter}
                filterInputHint={FilterInputHint}
                filterFromJson={values => ({
                    ...values,
                    from: values.tag ? generateFromDate(values.tag) : values.from,
                    // to: values.tag ? generateToDate(values.tag) : values.to,
                })}
                filterToJson={values => ({
                    ...values,
                    from: values.tag ? '' : values.from,
                    // to: values.tag ? '' : values.to,
                    icon: undefined,
                    title: undefined,
                })}
                inputPlaceholderKey={'email.search.placeholder'}
                localStorageKeyPrefix={'HammockFileEmailOverview'}
                reloadTrigger={emailReplicationFolderId}
                isItemSelectable={() => true}
                isItemSelected={item => selectedItems.includes(item)}
                onItemSelected={(data, item) => {
                    if (data.checked === true) {
                        setSelectedItems(prevItems => [...new Set([...prevItems, item])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== item.id)])])
                    }
                }}
                onItemClicked={props => {
                    if (selectedItems.includes(props.item)) {
                        setSelectedItems(prevItems => [...new Set([...prevItems.filter(prevItem => prevItem.id !== props.item.id)])])
                    } else {
                        setSelectedItems(prevItems => [...new Set([...prevItems, props.item])])
                    }
                }}
                {...props}
            />
        </>
    )
}
